// The Start Counter carry-forward read, shared by the two surfaces that edit a
// logbook entry: the always-editable detail page (LogbookEntry.jsx) and the
// pop-up editor (EditLogbookEntry.jsx). One hook rather than two copies,
// because the interesting part is not the query -- it is the lane wait below,
// and a surface that forgot it would answer with a stale number.
//
// Returns the `{ value, label, title }` suggestion Field renders as a button,
// or null when there is nothing to offer.

import { useEffect, useState } from 'react';
import * as Database from '../../utilities/Database.mjs';
import {
  carryForwardEndCounter,
  supportsCounterCarryForward,
} from '../../fleet-shared/LogbookTypes.mjs';
import { startCounterSuggestion } from '../../utilities/counterSuggestion';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { ENTRY_LANE_PREFIX } from '../../utilities/saveLanes';

export function usePreviousEndCounter({ logbookId, entry, typeId, enabled = true }) {
  const [suggestion, setSuggestion] = useState(null);

  // The entry's SAVED date, not the form's. That is what makes one rule work on
  // both surfaces: the modal keeps its form state inside EditForm, where a page
  // cannot reach it. It costs nothing on the detail page, where picking a date
  // saves immediately and the autosave's onSaved refreshes the entry -- so this
  // re-resolves on its own a moment later.
  const entryId = entry?.id ?? null;
  const entryDate = entry?.date ?? null;

  useEffect(() => {
    if (!enabled || !logbookId || !entryId || !entryDate || !supportsCounterCarryForward(typeId)) {
      setSuggestion(null);
      return undefined;
    }

    let mounted = true;
    // Lets the lane wait below be dropped on unmount instead of holding a queue
    // subscription until (or unless) the write finishes.
    const leaving = new AbortController();

    async function load() {
      try {
        // Read the lanes BEFORE the first await, and wait them out.
        //
        // Load-bearing rather than defensive here: the commonest way to reach
        // an entry with a blank Start Counter is to type the PREVIOUS entry's
        // End Counter and navigate straight to this one, which flushes that
        // edit as the page unmounts. A read that beats the write comes back
        // with no counter and the button silently never appears -- or, worse,
        // appears with the number the user just replaced.
        const lanes = saveQueue
          .pendingKeys()
          .filter((key) => key.startsWith(ENTRY_LANE_PREFIX));
        if (lanes.length > 0) {
          await saveQueue.settledAll(lanes, { signal: leaving.signal });
          if (!mounted) return;
        }

        const prior = await Database.previousLogbookEntry(logbookId, { id: entryId, date: entryDate });
        if (!mounted) return;

        setSuggestion(startCounterSuggestion(carryForwardEndCounter(prior, typeId), prior));
      } catch (err) {
        // Silence, not showError. This is a convenience on a page that is
        // otherwise fine, and a red banner over a failed *suggestion* would
        // read as the entry itself having failed to load.
        console.error('Could not read the previous logbook entry:', err);
        if (mounted) setSuggestion(null);
      }
    }

    load();
    return () => { mounted = false; leaving.abort(); };
  }, [logbookId, entryId, entryDate, typeId, enabled]);

  return suggestion;
}

export default usePreviousEndCounter;
